import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import { ManagedUserAccess } from '../Shared/ManagedUser';
import {
    ManagedApiError,
    ManagedFormErrorInfo,
    ManagedAdminChildOperation,
    ManagedActionModal,
    ManagedActionModalState,
    SlimTenantAdminModel,
    SlimTenantAdminExModel,
    SlimUserAccessReportModel,
    SlimUserAccessReportExModel,
    buildManagedActionModalState,
    mapSlimTenantAdminModel,
    mapSlimTenantAdminExModel,
    mapSlimUserAccessReportModel,
    mapSlimUserAccessReportExModel,
} from '../Shared/ManagedHelpers';
import { invalidateReportMenu } from '../Shared/ReportMenuQuery';
import GetTenantAdminModelsRequest from '../../Interfaces/Requests/Administration/GetTenantAdminModelsRequest';
import GetTenantAdminModelsExRequest from '../../Interfaces/Requests/Administration/GetTenantAdminModelsExRequest';
import GetReportAdminModelsRequest from '../../Interfaces/Requests/Administration/GetReportAdminModelsRequest';
import GetReportAdminModelsExRequest from '../../Interfaces/Requests/Administration/GetReportAdminModelsExRequest';
import GetTenantAdminModelsResponse from '../../Interfaces/Responses/Administration/GetTenantAdminModelResponse';
import GetTenantAdminModelsExResponse from '../../Interfaces/Responses/Administration/GetTenantAdminModelExResponse';
import GetReportAdminModelsResponse from '../../Interfaces/Responses/Administration/GetReportAdminModelResponse';
import GetReportAdminModelsExResponse from '../../Interfaces/Responses/Administration/GetReportAdminModelExResponse';
import UpdateUserTntsRptsModelRequest from '../../Interfaces/Requests/Administration/UpdateUserTntsRptsModelRequest';
import DeleteUserTntsRptsModelRequest from '../../Interfaces/Requests/Administration/DeleteUserTntsRptsModelRequest';
import Administration from '../../Constants/Endpoints/Administration';
import {
    Button,
    Col,
    Divider,
    Form,
    LeftArrowIcon,
    RightArrowIcon,
    Row,
    Space,
    Spin,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import { List } from 'antd';

export type ManagedUserAccessEditResult = {
    operation: ManagedAdminChildOperation;
    userAccesses: ManagedUserAccess[];
};

type AUDUserAccessFormValues = {
    uaUserAccessSelected?: ManagedUserAccess[];
};

type AUDUserAccessMutationVariables = {
    operation: ManagedAdminChildOperation;
    userAccesses: ManagedUserAccess[];
};

type AUDUserAccessUpdateMutationVariables = AUDUserAccessMutationVariables & {
    requestData: UpdateUserTntsRptsModelRequest[];
};

type AUDUserAccessDeleteMutationVariables = AUDUserAccessMutationVariables & {
    requestData: DeleteUserTntsRptsModelRequest;
};

type AUDUserAccessesProps = {
    userID: number;
    userName: string;
    userAccesses: ManagedUserAccess[];
    handleSetUserAccess: (
        result: ManagedUserAccessEditResult
    ) => void;
    close: () => void;
};

const AUDUserAccesses: React.FC<AUDUserAccessesProps> = ({ close, ...props }) => {
    const queryClient = useQueryClient();

    const [formAUDUserAccess] = Form.useForm();

    const userAccesses = props.userAccesses ?? [];

    const [uaSelectedTenantID, setUASelectedTenantID] = useState(0);
    const [uaAvailableReports, setUAAvailableReports] = useState<ManagedUserAccess[]>([]);
    const [uaSelectedUserAccesses, setUASelectedUserAccesses] = useState<ManagedUserAccess[]>(userAccesses);
    
    const uaSelectedTenantRef = useRef<HTMLDivElement | null>(null);

    const [uaIncludeDisabled] = useState(false);

    const [managedActionModalState, setManagedActionModalState] = useState<ManagedActionModalState>({
        open: false,
        mode: 'info',
        title: '',
        message: '',
        isError: false,
    });

    const uaPropsUserID = props.userID > 0 ? props.userID : 0;
    const uaPropsUserName = props.userName !== '' ? props.userName : 'Selected User';

    const getUAOperation = (): ManagedAdminChildOperation => {
        return 'update';
    };

    const getUAActionLabel = (): string => {
        return 'Update';
    };

    const getUAActionColor = (): string => {
        return 'darkgray';
    };

    const showUAInfoModal = (title: string, message: string, isError: boolean = true) => {
        setManagedActionModalState(buildManagedActionModalState('info', title, message, isError));
    };

    const showUAResultModal = (isError: boolean, message: string) => {
        setManagedActionModalState(
            buildManagedActionModalState(
                isError ? 'info' : getUAOperation(),
                `${getUAActionLabel()} User Access`,
                message,
                isError
            )
        );
    };

    const handleCloseManagedActionModal = () => {
        const shouldCloseEditor = (
            managedActionModalState.open &&
            !managedActionModalState.isError &&
            (
                managedActionModalState.mode === 'add' ||
                managedActionModalState.mode === 'update' ||
                managedActionModalState.mode === 'delete'
            )
        );

        setManagedActionModalState((currentState) => ({
            ...currentState,
            open: false,
        }));

        if (shouldCloseEditor) {
            close();
        }
    };

    const handleUALoadError = (title: string, err: unknown) => {
        console.error(title, err);

        const apiError = err as ManagedApiError;
        const message = apiError?.response?.data?.message
            ? `${title}: ${apiError.response.data.message}`
            : `${title}: ${String(err)}`;

        setManagedActionModalState(
            buildManagedActionModalState(
                'info',
                'User Access Load Error',
                message,
                true
            )
        );
    };

    const clearUASelection = () => {
        setUASelectedTenantID(0);
        setUAAvailableReports([]);
    };

    const { data: tenants = [], isLoading: isGetTenantsLoading } = useQuery<GetTenantAdminModelsResponse[], Error, SlimTenantAdminModel[]>(
        ['getTenantAdminModelsRequest', uaIncludeDisabled],
        async (): Promise<GetTenantAdminModelsResponse[]> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_TENANT_ADMIN_MODELS}`;
                const requestData: GetTenantAdminModelsRequest = { gta_includeDisabled: uaIncludeDisabled };
                return await AxiosUtil.get<GetTenantAdminModelsResponse[], GetTenantAdminModelsRequest>(url, requestData);
            } catch (err) {
                handleUALoadError('GET_TENANT_ADMIN_MODELS: [Get Tenant Admin Models] Internal Error', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: Error) => {
                handleUALoadError('GET_TENANT_ADMIN_MODELS: [Get Tenant Admin Models] Internal Error', err);
            },
            select: (response: GetTenantAdminModelsResponse[]): SlimTenantAdminModel[] => {
                return response
                    .map(mapSlimTenantAdminModel)
                    .filter((eachTenant) => !eachTenant.deleted)
                    .sort((a, b) => a.name.localeCompare(b.name));
            },
        }
    );

    const { data: tenantsEx = [], isLoading: isGetTenantsExLoading } = useQuery<GetTenantAdminModelsExResponse[], Error, SlimTenantAdminExModel[]>(
        ['getTenantAdminModelsExRequest', uaIncludeDisabled],
        async (): Promise<GetTenantAdminModelsExResponse[]> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_TENANT_ADMIN_MODELS_EX}`;
                const requestData: GetTenantAdminModelsExRequest = { gta_includeDisabled: uaIncludeDisabled };
                return await AxiosUtil.get<GetTenantAdminModelsExResponse[], GetTenantAdminModelsExRequest>(url, requestData);
            } catch (err) {
                handleUALoadError('GET_TENANT_ADMIN_MODELS_EX: [Get Tenant Admin Models Ex] Internal Error', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: Error) => {
                handleUALoadError('GET_TENANT_ADMIN_MODELS_EX: [Get Tenant Admin Models Ex] Internal Error', err);
            },
            select: (response: GetTenantAdminModelsExResponse[]): SlimTenantAdminExModel[] => {
                return response
                    .map(mapSlimTenantAdminExModel)
                    .filter((eachTenant) => !eachTenant.deleted)
                    .sort((a, b) => a.tenantName.localeCompare(b.tenantName));
            },
        }
    );

    const { data: reports = [], isLoading: isGetReportsLoading } = useQuery<GetReportAdminModelsResponse[], Error, SlimUserAccessReportModel[]>(
        ['getReportAdminModelsRequest', uaIncludeDisabled],
        async (): Promise<GetReportAdminModelsResponse[]> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_REPORT_ADMIN_MODELS}`;
                const requestData: GetReportAdminModelsRequest = { gra_includeDisabled: uaIncludeDisabled };
                return await AxiosUtil.get<GetReportAdminModelsResponse[], GetReportAdminModelsRequest>(url, requestData);
            } catch (err) {
                handleUALoadError('GET_REPORT_ADMIN_MODELS: [Get Report Admin Models] Internal Error', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: Error) => {
                handleUALoadError('GET_REPORT_ADMIN_MODELS: [Get Report Admin Models] Internal Error', err);
            },
            select: (response: GetReportAdminModelsResponse[]): SlimUserAccessReportModel[] => {
                return response
                    .map(mapSlimUserAccessReportModel)
                    .filter((eachReport) => !eachReport.deleted)
                    .sort((a, b) => `${a.reportGroupName}: ${a.reportName}`.localeCompare(`${b.reportGroupName}: ${b.reportName}`));
            },
        }
    );

    const { data: reportsEx = [], isLoading: isGetReportsExLoading } = useQuery<GetReportAdminModelsExResponse[], Error, SlimUserAccessReportExModel[]>(
        ['getReportAdminModelsExRequest', uaIncludeDisabled],
        async (): Promise<GetReportAdminModelsExResponse[]> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_REPORT_ADMIN_MODELS_EX}`;
                const requestData: GetReportAdminModelsExRequest = { gra_includeDisabled: uaIncludeDisabled };
                return await AxiosUtil.get<GetReportAdminModelsExResponse[], GetReportAdminModelsExRequest>(url, requestData);
            } catch (err) {
                handleUALoadError('GET_REPORT_ADMIN_MODELS_EX: [Get Report Admin Models Ex] Internal Error', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: Error) => {
                handleUALoadError('GET_REPORT_ADMIN_MODELS_EX: [Get Report Admin Models Ex] Internal Error', err);
            },
            select: (response: GetReportAdminModelsExResponse[]): SlimUserAccessReportExModel[] => {
                return response
                    .map(mapSlimUserAccessReportExModel)
                    .filter((eachReport) => !eachReport.deleted)
                    .sort((a, b) => `${a.reportGroupName}: ${a.reportName}`.localeCompare(`${b.reportGroupName}: ${b.reportName}`));
            },
        }
    );

    const buildUserAccessID = (tenantID: number, reportID: number): number => {
        return (tenantID * 1000000) + reportID;
    };

    const buildUserAccessTitle = (userAccess: ManagedUserAccess | undefined): string => {
        if (!userAccess) {
            return '';
        }

        return `[${userAccess.tenantID}] ${userAccess.tenantName} / [` +
            `${userAccess.reportID}] ${userAccess.reportGroupName}: ${userAccess.reportName}`;
    };

    const buildTenantLevelUserAccess = (tenantID: number): ManagedUserAccess => {
        const selectedTenant = tenants.find((tenant) => tenant.id === tenantID);

        return {
            id: buildUserAccessID(tenantID, 0),
            userID: uaPropsUserID,
            userName: uaPropsUserName,
            reportID: 0,
            reportName: '<All Reports>',
            reportGroupName: '<All Groups>',
            tenantID,
            tenantName: selectedTenant?.name ?? '',
            deleted: false,
        };
    };

    const buildReportLevelUserAccess = (report: SlimUserAccessReportModel): ManagedUserAccess => {
        return {
            id: buildUserAccessID(report.tenantID, report.reportID),
            userID: uaPropsUserID,
            userName: uaPropsUserName,
            reportID: report.reportID,
            reportName: report.reportName,
            reportGroupName: report.reportGroupName,
            tenantID: report.tenantID,
            tenantName: report.tenantName,
            deleted: false,
        };
    };

    const buildExistingTenantLevelUserAccess = (tenant: SlimTenantAdminExModel): ManagedUserAccess => {
        return {
            id: buildUserAccessID(tenant.tenantID, 0),
            userID: uaPropsUserID,
            userName: uaPropsUserName,
            reportID: 0,
            reportName: '<All Reports>',
            reportGroupName: '<All Groups>',
            tenantID: tenant.tenantID,
            tenantName: tenant.tenantName,
            deleted: false,
        };
    };

    const buildExistingReportLevelUserAccess = (report: SlimUserAccessReportExModel): ManagedUserAccess => {
        return {
            id: buildUserAccessID(report.tenantID, report.reportID),
            userID: uaPropsUserID,
            userName: uaPropsUserName,
            reportID: report.reportID,
            reportName: report.reportName,
            reportGroupName: report.reportGroupName,
            tenantID: report.tenantID,
            tenantName: report.tenantName,
            deleted: false,
        };
    };

    const getExistingUserAccesses = (): ManagedUserAccess[] => {
        const existingTenantAccesses = tenantsEx
            .filter((tenant) => tenant.userID === uaPropsUserID)
            .map(buildExistingTenantLevelUserAccess);

        const existingReportAccesses = reportsEx
            .filter((report) => report.userID === uaPropsUserID)
            .map(buildExistingReportLevelUserAccess);

        return [...existingTenantAccesses, ...existingReportAccesses]
            .sort((a, b) => buildUserAccessTitle(a).localeCompare(buildUserAccessTitle(b)));
    };

    useEffect(() => {
        if (isGetTenantsExLoading || isGetReportsExLoading) {
            return;
        }

        const existingTenantAccesses: ManagedUserAccess[] = tenantsEx
            .filter((tenant) => tenant.userID === uaPropsUserID)
            .map((tenant) => ({
                id: buildUserAccessID(tenant.tenantID, 0),
                userID: uaPropsUserID,
                userName: uaPropsUserName,
                reportID: 0,
                reportName: '<All Reports>',
                reportGroupName: '<All Groups>',
                tenantID: tenant.tenantID,
                tenantName: tenant.tenantName,
                deleted: false,
            }));

        const existingReportAccesses: ManagedUserAccess[] = reportsEx
            .filter((report) => report.userID === uaPropsUserID)
            .map((report) => ({
                id: buildUserAccessID(report.tenantID, report.reportID),
                userID: uaPropsUserID,
                userName: uaPropsUserName,
                reportID: report.reportID,
                reportName: report.reportName,
                reportGroupName: report.reportGroupName,
                tenantID: report.tenantID,
                tenantName: report.tenantName,
                deleted: false,
            }));

        setUASelectedUserAccesses(
            [...existingTenantAccesses, ...existingReportAccesses]
                .sort((a, b) => buildUserAccessTitle(a).localeCompare(buildUserAccessTitle(b)))
        );
    }, [
        isGetTenantsExLoading,
        isGetReportsExLoading,
        tenantsEx,
        reportsEx,
        uaPropsUserID,
        uaPropsUserName,
    ]);

    useEffect(() => {
        if (uaSelectedTenantID <= 0) {
            return;
        }

        uaSelectedTenantRef.current?.scrollIntoView({
            block: 'nearest',
            behavior: 'smooth',
        });
    }, [uaSelectedTenantID]);

    const getAvailableReportsForTenant = (
        tenantID: number,
        selectedUserAccesses: ManagedUserAccess[]
    ): ManagedUserAccess[] => {
        const selectedTenantReportAccesses = selectedUserAccesses.filter(
            (userAccess) => userAccess.tenantID === tenantID && userAccess.reportID !== 0
        );

        const selectedTenantAllAccess = selectedUserAccesses.find(
            (userAccess) => userAccess.tenantID === tenantID && userAccess.reportID === 0
        );

        if (selectedTenantAllAccess) {
            return [];
        }

        const availableReportsForTenant = reports
            .filter((report) => report.tenantID === tenantID)
            .filter((report) =>
                selectedTenantReportAccesses.every(
                    (selectedAccess) => selectedAccess.reportID !== report.reportID
                )
            )
            .map(buildReportLevelUserAccess);

        return [
            buildTenantLevelUserAccess(tenantID),
            ...availableReportsForTenant,
        ].sort((a, b) => buildUserAccessTitle(a).localeCompare(buildUserAccessTitle(b)));
    };
    
    const refreshUAAvailableReportsForTenant = (tenantID: number, selectedUserAccesses: ManagedUserAccess[]) => {
        if (uaSelectedTenantID !== tenantID) {
            setUASelectedTenantID(tenantID);
        }

        setUAAvailableReports([]);
        setUAAvailableReports(getAvailableReportsForTenant(tenantID, selectedUserAccesses));
    };

    const handleUATenantSelected = (tenantID: number) => {
        refreshUAAvailableReportsForTenant(tenantID, uaSelectedUserAccesses);
    };

    const handleUAReportSelected = (userAccess: ManagedUserAccess) => {
        if (userAccess.reportID === 0) {
            const updatedUserAccesses = [
                ...uaSelectedUserAccesses.filter((currentAccess) => currentAccess.tenantID !== userAccess.tenantID),
                userAccess,
            ].sort((a, b) => buildUserAccessTitle(a).localeCompare(buildUserAccessTitle(b)));

            setUASelectedUserAccesses(updatedUserAccesses);
            refreshUAAvailableReportsForTenant(userAccess.tenantID, updatedUserAccesses);
            return;
        }

        const updatedUserAccesses = [
            ...uaSelectedUserAccesses.filter(
                (currentAccess) => !(currentAccess.tenantID === userAccess.tenantID && currentAccess.reportID === 0)
            ),
            userAccess,
        ].sort((a, b) => buildUserAccessTitle(a).localeCompare(buildUserAccessTitle(b)));

        setUASelectedUserAccesses(updatedUserAccesses);
        refreshUAAvailableReportsForTenant(userAccess.tenantID, updatedUserAccesses);
    };

    const handleUASelectedUserAccessSelected = (userAccess: ManagedUserAccess) => {
        const updatedUserAccesses = uaSelectedUserAccesses
            .filter((currentAccess) => currentAccess.id !== userAccess.id)
            .sort((a, b) => buildUserAccessTitle(a).localeCompare(buildUserAccessTitle(b)));

        setUASelectedUserAccesses(updatedUserAccesses);
        refreshUAAvailableReportsForTenant(userAccess.tenantID, updatedUserAccesses);
    };

    const handleUAAvailableUserAccessSelected = (userAccessID: number) => {
        const selectedUserAccess = uaAvailableReports.find(
            (userAccess) => userAccess.id === userAccessID
        );

        if (!selectedUserAccess) {
            return;
        }

        handleUAReportSelected(selectedUserAccess);
    };

    const handleUASelectedUserAccessRemoved = (userAccessID: number) => {
        const selectedUserAccess = uaSelectedUserAccesses.find(
            (userAccess) => userAccess.id === userAccessID
        );

        if (!selectedUserAccess) {
            return;
        }

        handleUASelectedUserAccessSelected(selectedUserAccess);
    };

    const isUALoading = (
        isGetTenantsLoading ||
        isGetTenantsExLoading ||
        isGetReportsLoading ||
        isGetReportsExLoading
    );

    const buildUserAccessRequest = (userAccess: ManagedUserAccess): UpdateUserTntsRptsModelRequest => {
        return {
            reportID: userAccess.reportID,
            reportName: userAccess.reportName,
            reportGroupName: userAccess.reportGroupName,
            tenantID: userAccess.tenantID,
            tenantName: userAccess.tenantName,
            userID: uaPropsUserID,
        };
    };

    const buildDeleteUserAccessRequest = (): DeleteUserTntsRptsModelRequest => {
        return {
            userID: uaPropsUserID,
        };
    };

    const updateUserAccesses = useMutation(
        async (variables: AUDUserAccessUpdateMutationVariables) => {
            const url = `${process.env.REACT_APP_API_ROOT}${Administration.UPD_USER_TNTSRPTS_ADMIN}`;
            return await AxiosUtil.post<any, UpdateUserTntsRptsModelRequest[]>(url, variables.requestData);
        },
        {
            onSuccess: (_response, variables) => {
                props.handleSetUserAccess({
                    operation: variables.operation,
                    userAccesses: variables.userAccesses,
                });

                invalidateReportMenu(queryClient);
                showUAResultModal(false, 'User Accesses updated successfully.');
            },
            onError: (err: Error) => {
                console.error('UPD_USER_TNTSRPTS_ADMIN: [AUD User Access] Internal Error: ', err);
                showUAResultModal(true, 'Unable to update User Accesses.');
            },
        }
    );

    const deleteUserAccesses = useMutation(
        async (variables: AUDUserAccessDeleteMutationVariables) => {
            const url = `${process.env.REACT_APP_API_ROOT}${Administration.DEL_USER_TNTSRPTS_ADMIN}`;
            return await AxiosUtil.post<any, DeleteUserTntsRptsModelRequest>(url, variables.requestData);
        },
        {
            onSuccess: (_response, variables) => {
                props.handleSetUserAccess({
                    operation: variables.operation,
                    userAccesses: variables.userAccesses,
                });

                invalidateReportMenu(queryClient);
                showUAResultModal(false, 'User Accesses removed successfully.');
            },
            onError: (err: Error) => {
                console.error('DEL_USER_TNTSRPTS_ADMIN: [AUD User Access] Internal Error: ', err);
                showUAResultModal(true, 'Unable to remove User Accesses.');
            },
        }
    );

    const setUAFormFromExistingUserAccesses = () => {
        clearUASelection();
        setUASelectedUserAccesses(getExistingUserAccesses());
    };

    const handleUAUserAccessResetForm = () => {
        setUAFormFromExistingUserAccesses();
    };

    const validateUADetailsData = () => {
        if (uaPropsUserID <= 0) {
            showUAInfoModal('User Access Validation Error', 'A User must be selected before saving User Accesses.');
            return Promise.reject();
        }

        return Promise.resolve();
    };

    const onFinish = () => {
        validateUADetailsData()
            .then(() => {
                const userAccessesSnapshot = [...uaSelectedUserAccesses];

                if (userAccessesSnapshot.length === 0) {
                    deleteUserAccesses.mutate({
                        operation: 'update',
                        userAccesses: [],
                        requestData: buildDeleteUserAccessRequest(),
                    });

                    return;
                }

                updateUserAccesses.mutate({
                    operation: 'update',
                    userAccesses: userAccessesSnapshot,
                    requestData: userAccessesSnapshot.map(buildUserAccessRequest) as UpdateUserTntsRptsModelRequest[],
                });
            })
            .catch(() => {
                return;
            });
    };

    const onFinishFailed = (errorInfo: ManagedFormErrorInfo<AUDUserAccessFormValues>) => {
        console.error('AUDUserAccess validation failed:', errorInfo);
        showUAInfoModal('User Access Validation Error', 'Review the User Access selections and try again.');
    };

    const onUACancel = () => {
        updateUserAccesses.reset();
        deleteUserAccesses.reset();
        setUAFormFromExistingUserAccesses();
    };

    const accessNoteStyle: React.CSSProperties = {
        backgroundColor: '#fff7d6',
        border: '1px solid #e6c75a',
        borderRadius: '4px',
        color: 'dimgray',
        padding: '8px',
        margin: '4px',
        textAlign: 'center',
    };

    const contentStyle: React.CSSProperties = {
        lineHeight: '95%',
        textAlign: 'center',
        color: 'black',
        backgroundColor: 'azure',
        borderRadius: '10px',
        border: `1px solid black`,
        marginTop: '16px',
        height: '420px'
    };
    
    return (
        <>
            <ManagedActionModal
                modalState={managedActionModalState}
                onOk={handleCloseManagedActionModal}
            />
            <Spin spinning={isUALoading || updateUserAccesses.isLoading || deleteUserAccesses.isLoading}>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '4px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>Manage User Accesses</Title>
                    </Text>
                </Space>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={1} />
                    <Col span={22}>
                        <div style={accessNoteStyle}>
                            <Text italic>
                                User Access is managed as assignments between the selected User and Reports or Tenants.<br/>These rows are not ordinary child records owned only by the User; changing them updates access relationships that may also be visible from Report or Tenant administration.
                            </Text>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
                <Form
                    form={formAUDUserAccess}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                >
                    <div style={contentStyle}>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={21}>
                                <Divider orientation='left' orientationMargin={50} variant='dotted' style={{ borderColor: 'black', color: getUAActionColor() }}>
                                    Update User Access
                                </Divider>
                            </Col>
                            <Col span={3}>
                                <Button
                                    style={{ color: getUAActionColor(), fontStyle: 'italic' }}
                                    type='link'
                                    onClick={handleUAUserAccessResetForm}
                                >
                                    Reset Form
                                </Button>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={5}>
                                <div style={{ textAlign: 'center', width: '100%' }}>
                                    <h3 className='text-2xl pt-2 px-2'>Active Tenants</h3>
                                    <Text type='secondary'>Select an Active Tenant to view its Reports</Text>
                                </div>
                            </Col>
                            <Col span={1} />
                            <Col span={7}>
                                <div style={{ textAlign: 'center', width: '100%' }}>
                                    <h3 className='text-2xl pt-2 px-2'>Active Reports for Tenant</h3>
                                    <Text type='secondary'>Select Tenant/Report to Add to User</Text>
                                </div>
                            </Col>
                            <Col span={1} />
                            <Col span={8}>
                                <div style={{ textAlign: 'center', width: '100%' }}>
                                    <h3 className='text-2xl pt-2 px-2'>Tenants / Reports Selected</h3>
                                    <Text type='secondary'>Select Tenant/Report to Remove From User</Text>
                                </div>
                            </Col>
                            <Col span={1} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={5}>
                                <div style={{ border: '1px solid black', height: '28vh', overflow: 'auto', textAlign: 'left', verticalAlign: 'top' }}>
                                    <List
                                        dataSource={tenants}
                                        renderItem={(tenant) => (
                                            <List.Item
                                                key={tenant.id}
                                                ref={tenant.id === uaSelectedTenantID ? uaSelectedTenantRef : undefined}
                                                style={{
                                                    backgroundColor: tenant.id === uaSelectedTenantID ? 'lightsteelblue' : '',
                                                    border: tenant.id === uaSelectedTenantID ? '2px solid cornflowerblue' : '',
                                                    borderRadius: tenant.id === uaSelectedTenantID ? '6px' : '',
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() => { handleUATenantSelected(tenant.id); }}
                                            >
                                                <List.Item.Meta
                                                    title={<Button size='small' type='text'>{tenant.name}</Button>}
                                                />
                                            </List.Item>
                                        )}
                                        size='small'
                                    />
                                </div>
                            </Col>
                            <Col span={1}>
                                <br />
                                <br />
                                <br />
                                <br />
                                <RightArrowIcon style={{ verticalAlign: 'middle' }} />
                                <br />
                                <br />
                            </Col>
                            <Col span={7}>
                                <div style={{ border: '1px solid black', height: '28vh', overflow: 'auto', textAlign: 'left', verticalAlign: 'top' }}>
                                    <List
                                        dataSource={uaAvailableReports}
                                        renderItem={(userAccess) => (
                                            <List.Item
                                                key={userAccess.id}
                                                onClick={() => { handleUAAvailableUserAccessSelected(userAccess.id); }}
                                            >
                                                <List.Item.Meta
                                                    title={<Button size='small' type='text'>{userAccess.reportGroupName + ': ' + userAccess.reportName}</Button>}
                                                />
                                            </List.Item>
                                        )}
                                        size='small'
                                    />
                                </div>
                            </Col>
                            <Col span={1}>
                                <br />
                                <br />
                                <br />
                                <br />
                                <RightArrowIcon style={{ verticalAlign: 'middle' }} />
                                <br />
                                <LeftArrowIcon style={{ verticalAlign: 'middle' }} />
                            </Col>
                            <Col span={8}>
                                <div style={{ border: '1px solid black', height: '28vh', overflow: 'auto', textAlign: 'left', verticalAlign: 'top' }}>
                                    <Form.Item
                                        key='uauseraccessselected'
                                        name='uaUserAccessSelected'
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <List
                                            dataSource={uaSelectedUserAccesses}
                                            renderItem={(userAccess) => (
                                                <List.Item
                                                    key={userAccess.id}
                                                    onClick={() => { handleUASelectedUserAccessRemoved(userAccess.id); }}
                                                >
                                                    <List.Item.Meta
                                                        title={<Button size='small' type='text'>{buildUserAccessTitle(userAccess)}</Button>}
                                                    />
                                                </List.Item>
                                            )}
                                            size='small'
                                        />
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={1} />
                        </Row>
                    </div>
                    <Row gutter={16} className='retainbi-compact-row' style={{ paddingTop: '8px' }}>
                        <Col span={3}>
                            <Form.Item
                                name='uaCloseButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    style={{ width: '95%' }}
                                    type='default'
                                    onClick={close}
                                >
                                    Close
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col span={15} />
                        <Col span={3}>
                            <Form.Item
                                name='uaApplyButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    htmlType='submit'
                                    style={{ width: '95%', backgroundColor: getUAActionColor() }}
                                    type='primary'
                                >
                                    Update Changes
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col span={3}>
                            <Form.Item
                                name='uaCancelButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    style={{ width: '95%' }}
                                    type='default'
                                    onClick={onUACancel}
                                >
                                    Cancel Changes
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </>
    );
};

export default AUDUserAccesses;
